import { trackEvent, setAnalyticsUserProperties } from './firebase'
import { WordLength } from '../types/game'

/**
 * Game Analytics Service
 *
 * Provides specific methods for tracking user actions in the Zwizle game
 * based on the metrics outlined in the analytics tracking documentation.
 */

// Event name constants to ensure consistency
export const GameEvents = {
  // Core Gameplay Events
  GAME_STARTED: 'game_started',
  GAME_COMPLETED: 'game_completed',
  GAME_ABANDONED: 'game_abandoned',
  GUESS_SUBMITTED: 'guess_submitted',

  // Anagram events - kept separate from the Wordle events above rather than
  // parameterizing them, so Wordle's historical event schema stays
  // byte-identical (no retroactive "game" prop added to existing events).
  ANAGRAM_GAME_STARTED: 'anagram_game_started',
  ANAGRAM_GAME_COMPLETED: 'anagram_game_completed',
  ANAGRAM_MODE_SELECTED: 'anagram_mode_selected',

  // User Preference Events
  MODE_SELECTED: 'mode_selected',

  // Engagement Events
  RESULT_SHARED: 'result_shared',
  SWITCH_CHALLENGE: 'switch_challenge',
  PAGE_VIEW: 'page_view',

  // Problem Area Events
  ERROR_ENCOUNTERED: 'error_encountered',

  // User Journey Events
  FIRST_VISIT: 'first_visit',
  RETURN_VISIT: 'return_visit',
  INACTIVITY_TIMEOUT: 'inactivity_timeout',

  // User Interaction Events
  KEYBOARD_USAGE: 'keyboard_usage',
  INVALID_WORD_SUBMISSION: 'invalid_word_submission',
  HELP_VIEWED: 'help_viewed',

  // Performance Metrics
  LOAD_TIME_EXCEEDED: 'load_time_exceeded',
  API_FAILURE: 'api_failure',
  BROWSER_COMPATIBILITY_ISSUE: 'browser_compatibility_issue',

  // Feature Discovery
  THEME_CHANGED: 'theme_changed',
  SETTINGS_ADJUSTED: 'settings_adjusted',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  SHARE_ABANDONED: 'share_abandoned',

  // Session Quality Metrics
  SESSION_DURATION: 'session_duration',
  RAGE_QUIT_INDICATORS: 'rage_quit_indicators',
  SUCCESSFUL_WORD_PATTERNS: 'successful_word_patterns',

  // Technical Issues
  SCREEN_RESIZE_ISSUES: 'screen_resize_issues',
  OFFLINE_MODE_USAGE: 'offline_mode_usage',
  STORAGE_QUOTA_EXCEEDED: 'storage_quota_exceeded',
  VERSION_UPGRADE: 'version_upgrade',

  // User Behaviour Patterns
  TIME_PER_GUESS: 'time_per_guess',
  LETTER_DISTRIBUTION: 'letter_distribution',
  PLAY_TIME_DISTRIBUTION: 'play_time_distribution',
} as const

// Core Gameplay Metrics

/**
 * Track when a game is started
 */
export const trackGameStart = (wordLength: WordLength) => {
  trackEvent(GameEvents.GAME_STARTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when a game is completed (won or lost)
 */
export const trackGameCompletion = (
  wordLength: WordLength,
  won: boolean,
  attempts: number,
  timeTaken: number
) => {
  trackEvent(GameEvents.GAME_COMPLETED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    time_taken_seconds: Math.floor(timeTaken / 1000),
    timestamp: Date.now(),
  })
}

/**
 * Track when a player abandons a game
 */
export const trackGameAbandoned = (
  wordLength: WordLength,
  attemptsBeforeAbandoning: number
) => {
  trackEvent(GameEvents.GAME_ABANDONED, {
    word_length: wordLength,
    attempts_before_abandoning: attemptsBeforeAbandoning,
    timestamp: Date.now(),
  })
}

/**
 * Track when an Anagram game starts
 */
export const trackAnagramGameStart = (wordLength: WordLength) => {
  trackEvent(GameEvents.ANAGRAM_GAME_STARTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when an Anagram game is completed (won)
 */
export const trackAnagramGameCompletion = (
  wordLength: WordLength,
  won: boolean,
  attempts: number,
  timeTaken: number
) => {
  trackEvent(GameEvents.ANAGRAM_GAME_COMPLETED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    time_taken_seconds: Math.floor(timeTaken / 1000),
    timestamp: Date.now(),
  })
}

/**
 * Track when a user selects an Anagram mode
 */
export const trackAnagramModeSelected = (wordLength: WordLength) => {
  trackEvent(GameEvents.ANAGRAM_MODE_SELECTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when a guess is submitted
 */
export const trackGuessSubmitted = (
  wordLength: WordLength,
  guessNumber: number,
  correct: boolean
) => {
  trackEvent(GameEvents.GUESS_SUBMITTED, {
    word_length: wordLength,
    guess_number: guessNumber,
    correct: correct,
    timestamp: Date.now(),
  })
}

// User Preference Metrics

/**
 * Track when a user selects a game mode
 */
export const trackModeSelected = (wordLength: WordLength) => {
  trackEvent(GameEvents.MODE_SELECTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Set user device information as user properties
 */
export const setDeviceProperties = () => {
  // Only track information that's useful for UI optimization
  const properties: Record<string, string> = {
    device_type: isMobile() ? 'mobile' : 'desktop',
    viewport_width: `${window.innerWidth}px`,
    preferred_mode: localStorage.getItem('preferredMode') || 'unknown',
  }

  setAnalyticsUserProperties(properties)
}

/**
 * Track page views with pathname and additional parameters
 */
export const trackPageView = (pathname: string, additionalParams = {}) => {
  trackEvent(GameEvents.PAGE_VIEW, {
    page_path: pathname,
    page_title: getPageTitle(pathname),
    timestamp: Date.now(),
    ...additionalParams,
  })
}

/**
 * Helper function to get a readable page title from pathname
 */
function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Home - Mode Selection'
  if (pathname.startsWith('/play/')) {
    const mode = pathname.split('/').pop()
    return `${mode}-Letter Challenge`
  }
  if (pathname === '/complete') return 'Game Completion'
  return pathname
}

// Engagement Indicators

/**
 * Track when a user shares their result
 */
export const trackResultShared = (
  wordLength: WordLength,
  won: boolean,
  attempts: number
) => {
  trackEvent(GameEvents.RESULT_SHARED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user switches to another challenge
 */
export const trackChallengeSwitch = (
  fromWordLength: WordLength,
  toWordLength: WordLength
) => {
  trackEvent(GameEvents.SWITCH_CHALLENGE, {
    from_word_length: fromWordLength,
    to_word_length: toWordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when sharing is abandoned
 */
export const trackShareAbandoned = (
  wordLength: WordLength,
  won: boolean,
  attempts: number
) => {
  trackEvent(GameEvents.SHARE_ABANDONED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    timestamp: Date.now(),
  })
}

// Problem Area Metrics

/**
 * Track when a user encounters an error
 */
export const trackErrorEncountered = (
  errorType: string,
  errorMessage: string,
  wordLength?: WordLength
) => {
  trackEvent(GameEvents.ERROR_ENCOUNTERED, {
    error_type: errorType,
    error_message: errorMessage,
    word_length: wordLength || 0,
    timestamp: Date.now(),
  })
}

// User Journey Events

/**
 * Track a user's first visit to the app
 */
export const trackFirstVisit = () => {
  trackEvent(GameEvents.FIRST_VISIT, {
    timestamp: Date.now(),
    referrer: document.referrer || 'direct',
    user_agent: navigator.userAgent,
  })
}

/**
 * Track when a user returns to the app
 */
export const trackReturnVisit = (daysSinceLastVisit: number) => {
  trackEvent(GameEvents.RETURN_VISIT, {
    days_since_last_visit: daysSinceLastVisit,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user is inactive for a specified period
 */
export const trackInactivityTimeout = (
  inactiveTimeSeconds: number,
  currentPath: string
) => {
  trackEvent(GameEvents.INACTIVITY_TIMEOUT, {
    inactive_time_seconds: inactiveTimeSeconds,
    current_path: currentPath,
    timestamp: Date.now(),
  })
}

// User Interaction Events

/**
 * Track keyboard usage patterns
 */
export const trackKeyboardUsage = (
  key: string,
  wordLength: WordLength,
  isCorrect: boolean = false,
  isBackspace: boolean = false
) => {
  trackEvent(GameEvents.KEYBOARD_USAGE, {
    key: key,
    word_length: wordLength,
    is_correct: isCorrect,
    is_backspace: isBackspace,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user submits an invalid word
 */
export const trackInvalidWordSubmission = (
  wordLength: WordLength,
  invalidWord: string,
  attemptNumber: number
) => {
  trackEvent(GameEvents.INVALID_WORD_SUBMISSION, {
    word_length: wordLength,
    invalid_word: invalidWord,
    attempt_number: attemptNumber,
    timestamp: Date.now(),
  })
}

/**
 * Track when help or tutorial content is viewed
 */
export const trackHelpViewed = (helpSection: string) => {
  trackEvent(GameEvents.HELP_VIEWED, {
    help_section: helpSection,
    timestamp: Date.now(),
  })
}

// Performance Metrics

/**
 * Track when page load time exceeds threshold
 */
export const trackLoadTimeExceeded = (
  pageLoadTimeMs: number,
  pathname: string,
  threshold: number
) => {
  trackEvent(GameEvents.LOAD_TIME_EXCEEDED, {
    page_load_time_ms: pageLoadTimeMs,
    page_path: pathname,
    threshold_ms: threshold,
    timestamp: Date.now(),
  })
}

/**
 * Track API failures
 */
export const trackAPIFailure = (
  endpoint: string,
  errorMessage: string,
  statusCode?: number
) => {
  trackEvent(GameEvents.API_FAILURE, {
    endpoint: endpoint,
    error_message: errorMessage,
    status_code: statusCode || 0,
    timestamp: Date.now(),
  })
}

/**
 * Track browser compatibility issues
 */
export const trackBrowserCompatibilityIssue = (
  feature: string,
  browserInfo: string,
  errorDetails?: string
) => {
  trackEvent(GameEvents.BROWSER_COMPATIBILITY_ISSUE, {
    feature: feature,
    browser_info: browserInfo,
    error_details: errorDetails || '',
    timestamp: Date.now(),
  })
}

// Feature Discovery

/**
 * Track when theme or appearance settings are changed
 */
export const trackThemeChanged = (previousTheme: string, newTheme: string) => {
  trackEvent(GameEvents.THEME_CHANGED, {
    previous_theme: previousTheme,
    new_theme: newTheme,
    timestamp: Date.now(),
  })
}

/**
 * Track when user adjusts game settings
 */
export const trackSettingsAdjusted = (
  settingName: string,
  previousValue: string | number | boolean,
  newValue: string | number | boolean
) => {
  trackEvent(GameEvents.SETTINGS_ADJUSTED, {
    setting_name: settingName,
    previous_value: String(previousValue),
    new_value: String(newValue),
    timestamp: Date.now(),
  })
}

/**
 * Track when user submits feedback
 */
export const trackFeedbackSubmitted = (
  feedbackType: string,
  feedbackLength: number,
  sentiment: 'positive' | 'neutral' | 'negative'
) => {
  trackEvent(GameEvents.FEEDBACK_SUBMITTED, {
    feedback_type: feedbackType,
    feedback_length: feedbackLength,
    sentiment: sentiment,
    timestamp: Date.now(),
  })
}

// Session Quality Metrics

/**
 * Track session duration when a session ends
 */
export const trackSessionDuration = (
  durationSeconds: number,
  pageCount: number,
  completedGames: number
) => {
  trackEvent(GameEvents.SESSION_DURATION, {
    duration_seconds: durationSeconds,
    page_count: pageCount,
    completed_games: completedGames,
    timestamp: Date.now(),
  })
}

/**
 * Track potential rage quit indicators
 */
export const trackRageQuitIndicators = (
  wordLength: WordLength,
  consecutiveInvalidAttempts: number,
  rapidSubmissions: boolean,
  quitAfterAttempts: number
) => {
  trackEvent(GameEvents.RAGE_QUIT_INDICATORS, {
    word_length: wordLength,
    consecutive_invalid_attempts: consecutiveInvalidAttempts,
    rapid_submissions: rapidSubmissions,
    quit_after_attempts: quitAfterAttempts,
    timestamp: Date.now(),
  })
}

/**
 * Track successful word patterns
 */
export const trackSuccessfulWordPattern = (
  wordLength: WordLength,
  pattern: string,
  attemptsRequired: number
) => {
  trackEvent(GameEvents.SUCCESSFUL_WORD_PATTERNS, {
    word_length: wordLength,
    pattern: pattern,
    attempts_required: attemptsRequired,
    timestamp: Date.now(),
  })
}

// Technical Issues

/**
 * Track screen resize issues
 */
export const trackScreenResizeIssues = (
  previousWidth: number,
  newWidth: number,
  elementAffected: string
) => {
  trackEvent(GameEvents.SCREEN_RESIZE_ISSUES, {
    previous_width: previousWidth,
    new_width: newWidth,
    element_affected: elementAffected,
    timestamp: Date.now(),
  })
}

/**
 * Track when users play in offline mode
 */
export const trackOfflineModeUsage = (
  offlineDurationMinutes: number,
  actions: string[]
) => {
  trackEvent(GameEvents.OFFLINE_MODE_USAGE, {
    offline_duration_minutes: offlineDurationMinutes,
    actions_performed: actions.join(','),
    timestamp: Date.now(),
  })
}

/**
 * Track storage quota exceeded errors
 */
export const trackStorageQuotaExceeded = (
  quotaType: 'localStorage' | 'sessionStorage' | 'indexedDB',
  attemptedSize: number
) => {
  trackEvent(GameEvents.STORAGE_QUOTA_EXCEEDED, {
    quota_type: quotaType,
    attempted_size: attemptedSize,
    timestamp: Date.now(),
  })
}

/**
 * Track app version upgrades
 */
export const trackVersionUpgrade = (
  previousVersion: string,
  newVersion: string
) => {
  trackEvent(GameEvents.VERSION_UPGRADE, {
    previous_version: previousVersion,
    new_version: newVersion,
    timestamp: Date.now(),
  })
}

// User Behaviour Patterns

/**
 * Track time spent on each guess
 */
export const trackTimePerGuess = (
  wordLength: WordLength,
  guessNumber: number,
  timeSpentSeconds: number,
  isCorrect: boolean
) => {
  trackEvent(GameEvents.TIME_PER_GUESS, {
    word_length: wordLength,
    guess_number: guessNumber,
    time_spent_seconds: timeSpentSeconds,
    is_correct: isCorrect,
    timestamp: Date.now(),
  })
}

/**
 * Track letter distribution in guesses
 */
export const trackLetterDistribution = (
  wordLength: WordLength,
  letter: string,
  frequency: number,
  isCorrectPosition: boolean
) => {
  trackEvent(GameEvents.LETTER_DISTRIBUTION, {
    word_length: wordLength,
    letter: letter,
    frequency: frequency,
    is_correct_position: isCorrectPosition,
    timestamp: Date.now(),
  })
}

/**
 * Track play time distribution
 */
export const trackPlayTimeDistribution = (hourOfDay: number) => {
  trackEvent(GameEvents.PLAY_TIME_DISTRIBUTION, {
    hour_of_day: hourOfDay,
    day_of_week: new Date().getDay(),
    timestamp: Date.now(),
  })
}

// Helper Functions

/**
 * Simple mobile detection
 */
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}
